import "highlight.js/styles/github-dark-dimmed.css";
import React, { useMemo, useRef } from "react";

const AnimatedTextParent = ({ chunks }: { chunks: string[] }) => {
  return chunks.map((chunk, idx) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: not much other choice here. Edge cases handled in useAnimatedStreamText
    <span className="animate-stream-text-enter" key={idx}>
      {chunk}
    </span>
  ));
};

type ChunkedChild = { chunkId: number; chunks: string[]; input: string };
const isChunkedChild = (child: unknown): child is ChunkedChild => {
  return typeof child === "object" && child !== null && "chunkId" in child;
};

/**
 * Take children (strings and elements output by ReactMarkdown) and break up the text nodes into chunks that animate in independently.
 *
 * This is generally intended to be used for block level elements that render text, such as `p`. Inline
 * elements don't use it, because they require start/end tokens. So by the time we have the em element
 * for `hi *foo bar*`, we probably already streamed out `hi *foo b`, and we don't want to re-stream. To fix this,
 * ideally we'd chunk at element boundaries, but that would require better parsing in @see StreamHandler,
 * which is currently out of scope.
 *
 * TODO: In a world where we're actually doing streaming parsing, we could more tightly integrate this with StreamHandler.
 *
 * @see {@link ./README.md}
 */
export const useAnimatedStreamText = ({
  children: inputChildren,
  isStreaming,
}: {
  children: React.ReactNode;
  isStreaming: boolean;
}) => {
  const prevInputChildrenChunked = useRef<Array<React.ReactNode | ChunkedChild>>([]);

  const updatedChildren = useMemo(() => {
    // if we're not streaming, this is a no-op, do nothing
    if (!isStreaming) {
      return inputChildren;
    }

    // Map over the children, and convert text nodes into our itermediate `ChunkedChild` format
    const inputChildrenChunked = React.Children.map(inputChildren, (inputChild, idx) => {
      // Don't worry about child elements - they'll handle their own streaming if needed
      if (typeof inputChild !== "string") {
        return inputChild;
      }

      const prevChild = prevInputChildrenChunked.current?.at(idx);

      // if we've got some kind of weird diff happening, bail and render the inputChild as-is
      if (prevChild && !isChunkedChild(prevChild)) {
        return inputChild;
      }

      const prevChunked: ChunkedChild =
        (prevChild as ChunkedChild) ??
        ({ chunkId: Math.random(), chunks: [], input: "" } satisfies ChunkedChild);

      // The new data should be more text appended to what we used to build the previous chunk.
      // If that's not the case, it's likely some kind of markdown re-org happened, e.g. we went from `hi *t`
      // to `hi *this*`, and it's no longer a text node. In that case, fall back to just rendering the inputChild.
      if (
        prevChunked.input.length > inputChild.length ||
        !inputChild.startsWith(prevChunked.input)
      ) {
        return inputChild;
      }

      // ignore children that have not changed
      if (inputChild === prevChunked.input) {
        return prevChunked;
      }

      // Finally, we know we've got a text node that has gotten text added to it. We pull out that text
      // and add it as a new chunk.
      const newContent = inputChild.slice(prevChunked.input.length);
      const nextChunk: ChunkedChild = {
        ...prevChunked,
        input: inputChild,
        chunks: [...prevChunked.chunks, newContent],
      };
      return nextChunk;
    });

    // store this for next time
    prevInputChildrenChunked.current = inputChildrenChunked ?? [];

    // Remap over the children, and convert the intermediate "chunk" representation into the final animated text
    const outputChildren = inputChildrenChunked?.map((inputChild) => {
      if (!isChunkedChild(inputChild)) return inputChild;

      return <AnimatedTextParent chunks={inputChild.chunks} key={`chunk-${inputChild.chunkId}`} />;
    });

    return outputChildren;
  }, [inputChildren, isStreaming]);

  return updatedChildren;
};
